import type { CrawlResult, Violation, ViolationSignal } from '../lib/types';
import { FirecrawlClient } from '../lib/firecrawlClient';
import { OpenRouterClient, type LLMClassificationResult } from '../lib/openRouterClient';
import { Whitelist } from './whitelist';

export class Classifier {
  private static VIOLATION_THRESHOLD = 65;
  private static LLM_CONFIDENCE_THRESHOLD = 85;
  private static UGC_MIN_DURATION_MINUTES = 20;
  private static EPISODE_LIST_MIN_COUNT = 3;
  private static customDomainPatterns: RegExp[] | null = null;
  private static negativePriorDomains: Set<string> = new Set();
  private static negativePriorPatterns: RegExp[] = [];
  private static trustedNewsDomains: Set<string> = new Set();
  // Core piracy domain patterns reused across checks
  private static readonly DEFAULT_DOMAIN_PATTERNS: RegExp[] = [
    /(^|\.)egybest\./i,
    /(^|\.)arabseed\./i,
    /(^|\.)akwam\./i,
    /(^|\.)mycima\./i,
    /(^|\.)faselhd\./i,
    /(^|\.)movizland\./i,
    /(^|\.)fushaar\./i,
    /(^|\.)arablionz\./i,
    /(^|\.)almstba\./i,
    /(^|\.)honadrama\./i,
    /(^|\.)shoffree\./i,
    /(^|\.)egydead\./i,
    /(^|\.)cima\w*\./i,
  ];

  // Domain reputation tiers (Phase 2: reduce false positives)
  // DEPRECATED: Use trustedNewsDomains Set instead (configurable via settings)
  // Kept for backward compatibility fallback only
  private static readonly TRUSTED_NEWS_PATTERNS: RegExp[] = [
    /(^|\.)((nytimes|wsj|bbc|reuters|ap|bloomberg|theguardian|indiatimes|ft|telegraph)\.(com|co\.uk|org))$/i,
    /(^|\.)((theverge|ign|polygon|kotaku|gamespot|cnet|techcrunch)\.com)$/i,
    /(^|\.)((variety|hollywoodreporter|deadline|ew|indiewire|screendaily|primetimer)\.com)$/i,
  ];

  private static readonly KNOWN_PIRATES_BOOST: RegExp[] = [
    ...this.DEFAULT_DOMAIN_PATTERNS,
  ];

  // URL intent patterns (Phase 3: distinguish informational vs hosting)
  private static readonly INFORMATIONAL_PATH_PATTERNS: RegExp[] = [
    /\/(news|article|blog|post|features|review|reviews|guide|guides|schedule|episode-guide|release-schedule|recap|preview|trailer|announcement)\//i,
    /\/(about|coverage|report|story|editorial|opinion|analysis|explainer)\//i,
    /articleshow|newsarticle|press-release/i,
  ];

  private static readonly HOSTING_PATH_PATTERNS: RegExp[] = [
    /\/(watch|stream|download|play|embed|player|movie|tv|episode|season|series|video)\//i,
    /\/(view|streaming|downloads|media|content|library)\//i,
    /\d+x\d+/i, // Episode patterns like 1x01
  ];

  // Link destination patterns (Phase 4: analyze where links point)
  private static readonly LEGAL_STREAMING_PLATFORMS: RegExp[] = [
    /(^|\.)((netflix|hulu|disneyplus|hbomax|hbo|peacock|paramountplus)\.com)$/i,
    /(^|\.)((amazon|primevideo)\.com)$/i,
    /(^|\.)(tv\.)?apple\.com$/i,
    /(^|\.)youtube\.com$/i,
    /(^|\.)((showtime|starz|crunchyroll|funimation|vrv)\.com)$/i,
    /(^|\.)((max|sky)\.(com|co\.uk))$/i,
    /(^|\.)((nowtv)\.(com|co\.uk))$/i,
    /(^|\.)((channel4|bbc)\.co\.uk)$/i,
  ];

  private static readonly SUSPICIOUS_LINK_PATTERNS: RegExp[] = [
    /\.(mp4|mkv|avi|mov|wmv|flv|webm|m3u8|ts)($|\?)/i, // Direct video files
    /\/(stream|player|embed).*\.(mp4|m3u8)/i, // Streaming endpoints
    /\/video\/\d+/i, // Generic video IDs
  ];

  /**
   * Inject known-bad domain patterns from settings (regex strings)
   */
  static setDomainPatterns(patternStrings: string[] | null | undefined) {
    if (!patternStrings || patternStrings.length === 0) {
      this.customDomainPatterns = null;
      return;
    }
    try {
      this.customDomainPatterns = patternStrings
        .map((s) => s.trim())
        .filter(Boolean)
        .map((s) => new RegExp(s, 'i'));
    } catch (e) {
      console.warn('[Classifier] Failed to compile domain regex patterns from settings:', e);
      this.customDomainPatterns = null;
    }
  }

  /**
   * Inject negative priors (domains/patterns marked as false positives)
   */
  static setNegativePriors(domains: string[] | null | undefined, patterns?: string[] | null) {
    this.negativePriorDomains = new Set((domains || []).map(d => d.toLowerCase()));
    try {
      this.negativePriorPatterns = (patterns || []).map((p) => new RegExp(p, 'i'));
    } catch {
      this.negativePriorPatterns = [];
    }
  }

  /**
   * Inject trusted news/media domains from settings (configurable)
   */
  static setTrustedDomains(domains: string[] | null | undefined) {
    this.trustedNewsDomains = new Set((domains || []).map(d => d.toLowerCase()));
  }

  /**
   * Classify a crawled page for piracy violations
   */
  static classifyPage(crawlData: CrawlResult, contentTitle: string, debug = false): Violation | null {
    const signals: ViolationSignal[] = [];
    let totalScore = 0;
    const domain = new URL(crawlData.url).hostname;

    // Debug: Log what content was received (always show to debug scoring issues)
    console.log(`\n🔍 ANALYZING ${domain}:`);
    console.log(`  HTML length: ${crawlData.html?.length || 0} chars`);
    console.log(`  Text length: ${crawlData.text?.length || 0} chars`);
    console.log(`  Links count: ${crawlData.links?.length || 0}`);
    console.log(`  Page title: ${crawlData.title || 'N/A'}`);

    if ((crawlData.html?.length || 0) < 100) {
      console.log(`  ⚠️ WARNING: Very little HTML content received!`);
      console.log(`  Raw HTML: ${crawlData.html?.substring(0, 500) || 'EMPTY'}`);
    }

    if ((crawlData.text?.length || 0) < 50) {
      console.log(`  ⚠️ WARNING: Very little text content!`);
      console.log(`  Raw text: ${crawlData.text?.substring(0, 500) || 'EMPTY'}`);
    }

    // Title disambiguation: require strong mention of the exact title for multi‑word titles
    const normalized = (s: string) => (s || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0617-\u061A\u064B-\u0652]/g, '')
      .replace(/["'`’”“·•.,:;!?()\[\]{}_-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    const textAll = normalized((crawlData.title || '') + ' ' + (crawlData.text || ''));
    const titleNorm = normalized(contentTitle);
    const tokens = titleNorm.split(' ').filter(Boolean);
    const stopwords = new Set(['the','a','an','of','to','in','on','at','and','or','with','for','from','part','series','season']);
    const keywords = tokens.filter(t => !stopwords.has(t));
    if (keywords.some(k => !textAll.includes(k))) {
      return null;
    }

    // Detect if page is about a known different/conflicting title
    const confusableTitles: Record<string, string[]> = {
      'welcome to derry': ['derry girls'],
      'derry girls': ['welcome to derry'],
    };
    const titleLowerKey = titleNorm.toLowerCase().trim();
    const conflicts = confusableTitles[titleLowerKey] || [];
    for (const wrongTitle of conflicts) {
      if (textAll.includes(wrongTitle)) {
        console.log(`[Classifier] Page about "${wrongTitle}", not "${contentTitle}"`);
        return null; // Wrong title detected
      }
    }

    const requiresPhrase = tokens.length >= 3;
    if (requiresPhrase) {
      // Require the full title phrase or at least a contiguous trigram for strong match
      const hasFull = textAll.includes(titleNorm);

      // For 3+ word titles, require at least a 3-word sequence match
      const trigrams = tokens.map((_, i) => tokens.slice(i, i + 3).join(' ')).filter(s => s.split(' ').length === 3);
      const hasTrigram = trigrams.some(tg => tg && textAll.includes(tg));

      // Fallback to bigrams only if no trigram (for medium titles)
      const bigrams = tokens.map((_, i) => tokens.slice(i, i + 2).join(' ')).filter(s => s.split(' ').length === 2);
      const hasBigram = bigrams.some(bg => bg && textAll.includes(bg));

      if (!hasFull && !hasTrigram && (tokens.length < 4 || !hasBigram)) {
        return null; // Likely another work with overlapping tokens (e.g., "Derry Girls" vs "Welcome to Derry")
      }
    }

    // Check for known piracy domain patterns
    const domainSignal = this.detectPiracyDomain(domain, crawlData, contentTitle);
    if (domainSignal) {
      signals.push(domainSignal);
      totalScore += domainSignal.score;
    }

    // Apply negative signals first (e.g., trailers, promo pages / negative priors)
    const negativePenalty = this.detectLegitTrailerOrPromo(crawlData, contentTitle);
    if (negativePenalty) {
      signals.push(negativePenalty);
      totalScore += negativePenalty.score; // negative score reduces total
    }

    // Negative prior penalty if domain/url was previously marked false-positive
    if (this.isNegativePrior(domain, crawlData.url)) {
      signals.push({ type: 'negative_prior', score: -15, description: 'Previously marked as not pirate' });
      totalScore -= 15;
    }

    // Check for video players (nuanced scoring)
    const videoPlayerSignal = this.detectVideoPlayer(crawlData, domain);
    if (videoPlayerSignal) {
      signals.push(videoPlayerSignal);
      totalScore += videoPlayerSignal.score;
    }

    // Check for download links
    const downloadSignal = this.detectDownloadLinks(crawlData);
    if (downloadSignal) {
      signals.push(downloadSignal);
      totalScore += downloadSignal.score;
    }

    // Check for piracy keywords
    const keywordSignal = this.detectPiracyKeywords(crawlData, contentTitle);
    if (keywordSignal) {
      signals.push(keywordSignal);
      totalScore += keywordSignal.score;
    }

    // Check for suspicious ads
    const adSignal = this.detectSuspiciousAds(crawlData);
    if (adSignal) {
      signals.push(adSignal);
      // Ads alone should not push us over threshold; keep low
      totalScore += Math.min(adSignal.score, 10);
    }

    // Check for streaming embeds
    const embedSignal = this.detectStreamingEmbeds(crawlData);
    if (embedSignal) {
      signals.push(embedSignal);
      totalScore += embedSignal.score;
    }

    // Check page structure
    const structureSignal = this.detectPiracySiteStructure(crawlData);
    if (structureSignal) {
      signals.push(structureSignal);
      totalScore += structureSignal.score;
    }

    // Episode list detector (index pages of seasons/episodes)
    const episodeListSignal = this.detectEpisodeList(crawlData, contentTitle);
    if (episodeListSignal) {
      signals.push(episodeListSignal);
      totalScore += episodeListSignal.score;
    }

    // Explicit trailer in metadata (OG/JSON-LD) → penalize
    const trailerMetaPenalty = this.detectTrailerInMetadata(crawlData);
    if (trailerMetaPenalty) {
      signals.push(trailerMetaPenalty);
      totalScore += trailerMetaPenalty.score;
    }

    // Debug logging
    if (debug || totalScore >= 40) {
      console.log(`[Classifier] ${domain}:`);
      console.log(`  Score: ${totalScore}/100 (threshold: ${this.VIOLATION_THRESHOLD})`);
      console.log(`  Signals: ${signals.map(s => `${s.type}(+${s.score})`).join(', ') || 'none'}`);
      if (totalScore < this.VIOLATION_THRESHOLD) {
        console.log(`  ❌ FILTERED OUT - Score too low (need ${this.VIOLATION_THRESHOLD}+)`);
      } else {
        console.log(`  ✅ VIOLATION DETECTED`);
      }
    }

    // Evidence gating: reduce false positives where only generic signals are present
    const hasStrongEvidence = [downloadSignal, structureSignal, episodeListSignal].some(Boolean) ||
      (embedSignal && (keywordSignal || domainSignal)) ||
      (domainSignal && (keywordSignal || videoPlayerSignal));

    // If on social/UGC domains, require stronger evidence (duration/title cues)
    const socialDomains = /(^|\.)((youtube|youtu\.be|vimeo|dailymotion|facebook|x\.com|twitter|tiktok|instagram)\.com)$/i;
    const isSocial = socialDomains.test(domain.toLowerCase());
    const socialStrong = this.detectSocialStrongIndicators(crawlData, contentTitle);

    // News/media sites post official trailers, not piracy (filter out completely)
    const newsDomains = /(^|\.)((ign|gamespot|polygon|kotaku|theverge|cnet|techcrunch|variety|hollywoodreporter|deadline|entertainment|ew|collider|screenrant|comingsoon|movieweb)\.com)$/i;
    const isNews = newsDomains.test(domain.toLowerCase());

    // Q&A platforms discuss piracy but don't host it (filter out completely)
    const qaPlatforms = /(^|\.)((quora|stackexchange|stackoverflow|reddit|medium|wordpress|blogspot|tumblr)\.com)$/i;
    const isQA = qaPlatforms.test(domain.toLowerCase());

    // Apply domain reputation scoring (Phase 2)
    let adjustedScore = this.applyDomainReputation(domain, totalScore);
    if (adjustedScore !== totalScore) {
      console.log(`[Reputation] ${domain}: ${totalScore} → ${adjustedScore} (${adjustedScore > totalScore ? 'boosted' : 'reduced'})`);
    }

    // Apply URL intent classification (Phase 3)
    const intentAdjustedScore = this.applyIntentClassification(crawlData.url, adjustedScore);
    if (intentAdjustedScore !== adjustedScore) {
      console.log(`[Intent] ${crawlData.url}: ${adjustedScore} → ${intentAdjustedScore} (${intentAdjustedScore > adjustedScore ? 'hosting' : 'informational'})`);
      adjustedScore = intentAdjustedScore;
    }

    // Apply link destination analysis (Phase 4)
    const linkAdjustedScore = this.analyzeLinkDestinations(crawlData, adjustedScore);
    if (linkAdjustedScore !== adjustedScore) {
      console.log(`[Links] ${crawlData.url}: ${adjustedScore} → ${linkAdjustedScore} (${linkAdjustedScore > adjustedScore ? 'suspicious' : 'legal platforms'})`);
      adjustedScore = linkAdjustedScore;
    }

    const meetsThreshold = adjustedScore >= this.VIOLATION_THRESHOLD;
    if (!meetsThreshold || (isSocial && !socialStrong) || isNews || isQA) {
      return null;
    }

    // Generate human-readable blurb
    const blurb = this.generateBlurb(signals, crawlData);

    return {
      url: crawlData.url,
      domain,
      score: adjustedScore,
      signals: signals.map(s => s.type),
      blurb,
      pageTitle: crawlData.title,
      detectedAt: Date.now(),
    };
  }

  /**
   * Detect known piracy domains and patterns
   */
  private static detectPiracyDomain(domain: string, crawlData: CrawlResult, contentTitle: string): ViolationSignal | null {
    const title = (crawlData.title || '').toLowerCase();
    const titleLower = (contentTitle || '').toLowerCase();
    const host = domain.toLowerCase();

    // Skip if on whitelist
    if (Whitelist.isWhitelisted(host)) {
      return null;
    }

    // Known piracy domains/patterns (from settings or defaults)
    const domainPatterns: RegExp[] = this.customDomainPatterns || this.DEFAULT_DOMAIN_PATTERNS;

    const matchesKnown = domainPatterns.some(rx => rx.test(host));

    if (matchesKnown) {
      // Check if page has streaming/series indicators
      const hasStreamingIndicators =
        /مشاهدة|مسلسل|فيلم|حلقة|الحلقة|موسم|الموسم|تحميل|watch|episode|season/i.test(title) ||
        /مشاهدة|مسلسل|فيلم|حلقة|الحلقة|موسم|الموسم|تحميل|watch|episode|season/i.test(crawlData.text || '');

      // For known piracy domains, only require basic streaming indicators
      if (hasStreamingIndicators) {
        return {
          type: 'known_piracy_domain',
          score: 40,
          description: `Known piracy domain pattern detected`,
        };
      }
    }

    return null;
  }

  /** Lightweight: check if host matches known piracy patterns */
  static isKnownPiracyHost(host: string): boolean {
    const patterns = this.customDomainPatterns || this.DEFAULT_DOMAIN_PATTERNS;
    const h = (host || '').toLowerCase();
    return patterns.some(rx => {
      try { return rx.test(h); } catch { return false; }
    });
  }

  /**
   * Detect video players in HTML with nuanced scoring
   */
  private static detectVideoPlayer(crawlData: CrawlResult, domain: string): ViolationSignal | null {
    const players = FirecrawlClient.detectVideoPlayers(crawlData.html);
    if (players.length === 0) return null;

    const text = (crawlData.text + ' ' + crawlData.html).toLowerCase();
    const isDirectFile = /\.(m3u8|mp4)(\?|\b)/i.test(text);
    const isYouTube = players.length === 1 && players[0] === 'YouTube';
    const isDailymotion = /dailymotion/i.test(domain) || (players.length === 1 && players[0] === 'Dailymotion');

    if (isYouTube || isDailymotion) {
      return { type: 'video_player_ugc', score: 0, description: `${players[0]} embed detected (UGC)` };
    }

    if (isDirectFile) {
      return { type: 'video_player_direct', score: 40, description: 'Direct media stream detected (mp4/m3u8)' };
    }

    const score = Math.min(25, 10 + players.length * 5);
    return { type: 'video_player', score, description: `Video player detected: ${players.join(', ')}` };
  }

  /**
   * Detect download links
   */
  private static detectDownloadLinks(crawlData: CrawlResult): ViolationSignal | null {
    const downloadLinks = FirecrawlClient.extractDownloadLinks(
      crawlData.links,
      crawlData.html
    );

    if (downloadLinks.length === 0) {
      return null;
    }

    // Only treat as strong signal if links look like video/media archives, not documents
    const videoExt = /(\.mp4|\.mkv|\.avi|\.mov|\.m3u8|\.ts|\.webm)(\b|\?|#)/i;
    const docExt = /(\.pdf|\.epub|\.mobi|\.cbz|\.cbr)(\b|\?|#)/i;
    let videoCount = 0, docCount = 0;
    for (const href of downloadLinks as any[]) {
      const s = String(href || '');
      if (videoExt.test(s)) videoCount++;
      if (docExt.test(s)) docCount++;
    }

    if (videoCount > 0) {
      const score = 35 + Math.min(15, (videoCount - 1) * 5); // boost video downloads
      return { type: 'download_links', score, description: `${videoCount} video download link(s) found` };
    }

    // If only documents detected, do not score as piracy
    if (docCount > 0) {
      return { type: 'non_video_downloads', score: -10, description: `${docCount} document download(s) (pdf/epub) — not video piracy` };
    }

    // Generic downloads but no clear extension — keep small weight
    return { type: 'download_links', score: 10, description: `${downloadLinks.length} ambiguous download link(s)` };
  }

  /**
   * Negative signal: trailers/promo pages should not be classified as piracy
   */
  private static detectLegitTrailerOrPromo(crawlData: CrawlResult, contentTitle: string): ViolationSignal | null {
    const title = (crawlData.title || '').toLowerCase();
    const text = (crawlData.text + ' ' + crawlData.html).toLowerCase();
    const negativeKeywords = [
      'trailer', 'official trailer', 'teaser', 'promo', 'preview',
      'اعلان', 'إعلان', 'برومو', 'تيزر'
    ];
    const hasNegative = negativeKeywords.some(k => title.includes(k) || text.includes(k));
    if (hasNegative) {
      return { type: 'trailer_or_promo', score: -50, description: 'Trailer/Promo indicators detected' };
    }
    return null;
  }

  /**
   * For social/UGC domains: require strong indicators
   */
  private static detectSocialStrongIndicators(crawlData: CrawlResult, contentTitle: string): boolean {
    const text = (crawlData.text + ' ' + crawlData.html).toLowerCase();
    const title = (crawlData.title || '').toLowerCase();
    const titleMatch = title.includes((contentTitle || '').toLowerCase());
    const episodePattern = /(s\d{1,2}\s*e\d{1,2})|season\s*\d+|episode\s*\d+|الحلقة\s*\d+/i;
    const qualityPattern = /(1080p|720p|480p)/i;
    const freeKeywords = /(watch\s*free|stream\s*free|download\s*free|مشاهدة\s*مجانا|تحميل\s*مباشر)/i;
    const hasEpisode = episodePattern.test(title) || episodePattern.test(text);
    const hasQuality = qualityPattern.test(text);
    const hasFree = freeKeywords.test(text);
    const duration = this.extractVideoDurationSeconds(crawlData);
    const longEnough = duration !== null ? duration >= Classifier.UGC_MIN_DURATION_MINUTES * 60 : false;
    return titleMatch && (hasEpisode || (hasQuality && hasFree) || longEnough);
  }

  /** Parse video duration from meta tags or content */
  private static extractVideoDurationSeconds(crawlData: CrawlResult): number | null {
    const html = crawlData.html || '';
    const text = (crawlData.text || '') + ' ' + html;

    // meta property="og:video:duration" content="1234"
    const ogMatch = html.match(/property=["']og:video:duration["'][^>]*content=["'](\d+)["']/i) ||
                    html.match(/content=["'](\d+)["'][^>]*property=["']og:video:duration["']/i);
    if (ogMatch) {
      const sec = parseInt(ogMatch[1], 10);
      if (!isNaN(sec) && sec > 0) return sec;
    }

    // itemprop="duration" content="PT23M10S"
    const isoMatch = html.match(/itemprop=["']duration["'][^>]*content=["'](PT[\dHMS]+)["']/i) ||
                     html.match(/content=["'](PT[\dHMS]+)["'][^>]*itemprop=["']duration["']/i);
    if (isoMatch) {
      const sec = this.parseISODurationSeconds(isoMatch[1]);
      if (sec !== null) return sec;
    }

    // Plain text duration 00:23:45 or 23:00
    const timeMatch = text.match(/\b(\d{1,2}):(\d{2})(?::(\d{2}))?\b/);
    if (timeMatch) {
      const h = timeMatch[3] ? parseInt(timeMatch[1], 10) : 0;
      const m = timeMatch[3] ? parseInt(timeMatch[2], 10) : parseInt(timeMatch[1], 10);
      const s = timeMatch[3] ? parseInt(timeMatch[3], 10) : parseInt(timeMatch[2], 10);
      const total = h * 3600 + m * 60 + s;
      if (total > 0) return total;
    }
    return null;
  }

  private static parseISODurationSeconds(iso: string): number | null {
    const m = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (!m) return null;
    const h = parseInt(m[1] || '0', 10);
    const min = parseInt(m[2] || '0', 10);
    const s = parseInt(m[3] || '0', 10);
    return h * 3600 + min * 60 + s;
  }

  private static isNegativePrior(domain: string, url: string): boolean {
    const d = domain.toLowerCase();
    if (this.negativePriorDomains.has(d)) return true;
    for (const rx of this.negativePriorPatterns) {
      try { if (rx.test(url)) return true; } catch {}
    }
    return false;
  }

  /**
   * Apply domain reputation multiplier (Phase 2: reduce false positives)
   * Trusted news sites get 0.1x multiplier (90% reduction)
   * Known pirates get 1.5x boost
   */
  private static applyDomainReputation(domain: string, baseScore: number): number {
    const d = domain.toLowerCase();

    // Check configurable trusted domains first (exact match or parent domain)
    if (this.trustedNewsDomains.size > 0) {
      for (const trusted of this.trustedNewsDomains) {
        if (d === trusted || d.endsWith(`.${trusted}`)) {
          return Math.round(baseScore * 0.1);
        }
      }
    } else {
      // Fallback to hardcoded patterns if not configured
      if (this.TRUSTED_NEWS_PATTERNS.some(p => p.test(d))) {
        return Math.round(baseScore * 0.1);
      }
    }

    // Known piracy domains - boost confidence
    if (this.KNOWN_PIRATES_BOOST.some(p => p.test(d))) {
      return Math.round(baseScore * 1.5);
    }

    // Unknown domains - use original score
    return baseScore;
  }

  /**
   * Apply URL intent classification (Phase 3: distinguish informational vs hosting)
   * Informational paths (news, articles, guides) get 0.2x multiplier (80% reduction)
   * Hosting paths (watch, stream, download) get 1.3x boost
   */
  private static applyIntentClassification(url: string, baseScore: number): number {
    const urlLower = url.toLowerCase();

    // Informational content - unlikely to be hosting piracy
    if (this.INFORMATIONAL_PATH_PATTERNS.some(p => p.test(urlLower))) {
      return Math.round(baseScore * 0.2);
    }

    // Hosting/streaming paths - likely piracy if other signals present
    if (this.HOSTING_PATH_PATTERNS.some(p => p.test(urlLower))) {
      return Math.round(baseScore * 1.3);
    }

    // Neutral URLs - use original score
    return baseScore;
  }

  /**
   * Analyze link destinations (Phase 4: check where links point)
   * Pages linking to legal platforms get 0.3x multiplier (70% reduction)
   * Pages with suspicious video links get 1.4x boost
   */
  private static analyzeLinkDestinations(crawlData: CrawlResult, baseScore: number): number {
    const links = crawlData.links || [];
    if (links.length === 0) return baseScore;

    let legalPlatformLinks = 0;
    let suspiciousLinks = 0;

    for (const link of links) {
      try {
        const linkLower = link.toLowerCase();

        // Extract hostname from URL for domain pattern matching
        // crawlData.links contains full URLs like "https://netflix.com/watch/123"
        let hostname = '';
        try {
          // Handle different URL formats:
          // - http:// or https:// → use as-is
          // - //domain.com → protocol-relative, prepend https:
          // - domain.com → prepend https://
          let normalizedUrl = linkLower;
          if (linkLower.startsWith('//')) {
            normalizedUrl = `https:${linkLower}`;
          } else if (!linkLower.startsWith('http')) {
            normalizedUrl = `https://${linkLower}`;
          }

          const urlObj = new URL(normalizedUrl);
          hostname = urlObj.hostname.replace(/^www\./, '');
        } catch {
          // If URL parsing fails, try to extract domain from relative or malformed URLs
          hostname = linkLower.replace(/^(https?:)?\/\//, '').replace(/^www\./, '').split('/')[0];
        }

        // Check for legal streaming platforms (test against hostname, not full URL)
        if (hostname && this.LEGAL_STREAMING_PLATFORMS.some(p => p.test(hostname))) {
          legalPlatformLinks++;
          console.log(`[Links] Legal platform detected: ${hostname} from ${link}`);
        }

        // Check for suspicious patterns (test against full URL for path patterns)
        if (this.SUSPICIOUS_LINK_PATTERNS.some(p => p.test(linkLower))) {
          suspiciousLinks++;
          console.log(`[Links] Suspicious link detected: ${link}`);
        }
      } catch (e) {
        console.warn('[Links] Error processing link:', link, e);
      }
    }

    // If page links to legal platforms (likely "where to watch" article)
    if (legalPlatformLinks >= 2) {
      return Math.round(baseScore * 0.3);
    }

    // If page has suspicious video links (likely piracy)
    if (suspiciousLinks >= 1) {
      return Math.round(baseScore * 1.4);
    }

    // No strong signal from links
    return baseScore;
  }

  /**
   * Detect episode/season index structure by scanning links and HTML
   */
  private static detectEpisodeList(crawlData: CrawlResult, _contentTitle: string): ViolationSignal | null {
    const links = crawlData.links || [];
    const html = crawlData.html || '';

    const epUrlPatterns = [
      /(season|s)[-_\s]?\d{1,2}[^\w]?(episode|ep|e)[-_\s]?\d{1,3}/i,
      /\/season-\d+\/episode-\d+/i,
      /\b(s\d{1,2}\s*e\d{1,2})\b/i,
      /الحلقة\s*\d+/i,
    ];

    let epLinkCount = 0;
    for (const url of links) {
      if (epUrlPatterns.some((rx) => rx.test(url))) epLinkCount++;
    }
    // Also count occurrences in HTML (menus/lists without full URLs)
    const htmlMatches = (html.match(/الحلقة\s*\d+/gi) || []).length + (html.match(/S\d{1,2}\s*E\d{1,2}/gi) || []).length;
    epLinkCount += Math.floor(htmlMatches / 3);

    if (epLinkCount >= Classifier.EPISODE_LIST_MIN_COUNT) {
      const score = Math.min(30, 10 + epLinkCount * 3);
      return { type: 'episode_list', score, description: `Episode list detected (${epLinkCount} links)` };
    }
    return null;
  }

  /**
   * Penalize pages that explicitly declare "trailer" in OG/structured metadata
   */
  private static detectTrailerInMetadata(crawlData: CrawlResult): ViolationSignal | null {
    const html = crawlData.html || '';
    const lower = html.toLowerCase();
    const trailerWords = ['trailer', 'official trailer', 'teaser', 'promo', 'preview', 'اعلان', 'إعلان', 'برومو', 'تيزر'];

    // og:title or twitter:title
    const metaTitles = [
      ...Array.from(lower.matchAll(/<meta[^>]+property=["']og:title["'][^>]+content=["']([^"']+)/g)).map(m => m[1]),
      ...Array.from(lower.matchAll(/<meta[^>]+name=["']twitter:title["'][^>]+content=["']([^"']+)/g)).map(m => m[1]),
    ];
    const hasTrailerInMeta = metaTitles.some(t => trailerWords.some(w => t.includes(w)));

    // JSON-LD VideoObject name
    let hasTrailerInJsonLd = false;
    for (const m of lower.matchAll(/<script[^>]+type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/g)) {
      try {
        const json = JSON.parse(m[1]);
        const items = Array.isArray(json) ? json : [json];
        for (const item of items) {
          if (item['@type'] && String(item['@type']).toLowerCase().includes('videoobject')) {
            const name = String(item.name || item.headline || '').toLowerCase();
            if (trailerWords.some(w => name.includes(w))) { hasTrailerInJsonLd = true; break; }
          }
        }
      } catch {}
      if (hasTrailerInJsonLd) break;
    }

    if (hasTrailerInMeta || hasTrailerInJsonLd) {
      return { type: 'trailer_metadata', score: -25, description: 'Trailer indicated in metadata' };
    }
    return null;
  }

  /**
   * Detect piracy keywords in English and Arabic
   */
  private static detectPiracyKeywords(
    crawlData: CrawlResult,
    contentTitle: string
  ): ViolationSignal | null {
    const text = (crawlData.text + ' ' + crawlData.html).toLowerCase();
    const title = crawlData.title?.toLowerCase() || '';

    const piracyKeywords = [
      // English
      'watch free','free streaming','download full','free download','watch online free','stream for free',
      'full episode','full movie','no subscription','without paying','hd 1080p','1080p','720p','torrent','magnet link',
      'mirror','multi server','server 1','server 2','direct download','mp4 download','m3u8',

      // Arabic - Expanded
      'مشاهدة مجانا',      // watch free
      'مشاهدة كاملة',      // full watch
      'جميع الحلقات',      // all episodes
      'مشاهدة اونلاين',    // watch online alt
      'سيرفر سريع',        // fast server
      'روابط متعددة',      // multiple links
      'تحميل مترجم',       // translated download
      'تحميل الحلقة',       // download episode
      'تحميل مجاني',       // free download
      'بدون اشتراك',       // without subscription
      'مجانا بالكامل',     // completely free
      'تحميل مباشر',       // direct download
      'بدون دفع',          // without payment
      'مشاهدة اون لاين',   // watch online
      'اون لاين مترجم',    // online translated
      'مترجم كامل',        // translated complete
      'مشاهدة مباشرة',     // direct watch
      'بدون تسجيل',        // without signup
      'بدون تحميل',        // without download
      'نسخة مترجمة',       // translated version
    ];

    // Check if content title appears
    const titleLower = contentTitle.toLowerCase();
    const titleInPage = text.includes(titleLower) || title.includes(titleLower);

    if (!titleInPage) {
      return null;
    }

    const matchedKeywords = piracyKeywords.filter(keyword =>
      text.includes(keyword.toLowerCase()) || title.includes(keyword.toLowerCase())
    );

    // Lower threshold for Arabic sites with translated content
    const hasArabicTranslatedPattern = /مترجم|مشاهدة.*اون لاين|مشاهدة.*مباشر/i.test(text) ||
                                       /مترجم|مشاهدة.*اون لاين|مشاهدة.*مباشر/i.test(title);

    if (matchedKeywords.length >= 2 || (matchedKeywords.length >= 1 && hasArabicTranslatedPattern)) {
      return {
        type: 'piracy_keywords',
        score: 20,
        description: `Piracy indicators: ${matchedKeywords.length} keyword(s) + streaming pattern`,
      };
    }

    return null;
  }

  /**
   * Detect suspicious ad networks common on piracy sites
   */
  private static detectSuspiciousAds(crawlData: CrawlResult): ViolationSignal | null {
    const html = crawlData.html.toLowerCase();

    const suspiciousAdNetworks = [
      'popcash',
      'popads',
      'propellerads',
      'adcash',
      'clickadu',
      'exoclick',
      'trafficjunky',
      'juicyads',
      'adskeeper',
      'mgid',
      'adsterra',
      'hilltopads',
      'pushnami',
    ];

    const detectedAds = suspiciousAdNetworks.filter(ad => html.includes(ad));

    if (detectedAds.length > 0) {
      return {
        type: 'suspicious_ads',
        score: 10,
        description: `Suspicious ad networks: ${detectedAds.join(', ')}`,
      };
    }

    return null;
  }

  /**
   * Detect streaming service embeds
   */
  private static detectStreamingEmbeds(crawlData: CrawlResult): ViolationSignal | null {
    const html = crawlData.html.toLowerCase();

    const embedPatterns = [
      'embed.php',
      'player.php',
      'stream.php',
      '/embed/',
      '/player/',
      'streamtape',
      'mixdrop',
      'upstream',
      'doodstream',
      'streamlare',
      'vidoza',
      'uqload',
      'voe.sx',
      'filemoon',
      'streamwish',
      'sbembed',
      'vidcloud',
      'streamsb',
      'megaup',
      'ok.ru/video',
      'mega.nz',
      'mediafire.com',
      '1fichier.com',
      'rapidgator.net',
    ];

    const matches = embedPatterns.filter(pattern => html.includes(pattern));

    if (matches.length > 0) {
      return {
        type: 'streaming_embeds',
        score: 25,
        description: `Third-party streaming embeds detected`,
      };
    }

    return null;
  }

  /**
   * Detect typical piracy site structure
   */
  private static detectPiracySiteStructure(crawlData: CrawlResult): ViolationSignal | null {
    const text = crawlData.text.toLowerCase();
    const html = crawlData.html.toLowerCase();
    let structureScore = 0;

    // Multiple quality options (360p, 480p, 720p, 1080p)
    const qualityPattern = /(360p|480p|720p|1080p|2160p)/gi;
    const qualityMatches = text.match(qualityPattern);
    if (qualityMatches && qualityMatches.length >= 3) {
      structureScore += 5;
    }

    // Season/episode listings
    if (/season \d+|episode \d+|s\d+e\d+/i.test(text)) {
      structureScore += 3;
    }

    // Multiple server options
    if (/server \d+|mirror \d+/i.test(text)) {
      structureScore += 5;
    }

    // Subtitle options
    if (/subtitle|sub|مترجم/i.test(text)) {
      structureScore += 2;
    }

    if (structureScore >= 5) {
      return {
        type: 'piracy_structure',
        score: structureScore,
        description: 'Page structure typical of piracy sites',
      };
    }

    return null;
  }

  /**
   * Generate human-readable blurb from signals
   */
  private static generateBlurb(
    signals: ViolationSignal[],
    crawlData: CrawlResult
  ): string {
    const parts: string[] = [];

    for (const signal of signals) {
      switch (signal.type) {
        case 'known_piracy_domain':
          parts.push('known piracy site');
          break;
        case 'video_player':
          parts.push('embedded streaming player');
          break;
        case 'download_links':
          parts.push('direct download links');
          break;
        case 'piracy_keywords':
          parts.push('explicit free streaming claims');
          break;
        case 'streaming_embeds':
          parts.push('third-party streaming service');
          break;
        case 'piracy_structure':
          parts.push('multi-quality/server options');
          break;
        case 'suspicious_ads':
          parts.push('suspicious advertising');
          break;
      }
    }

    if (parts.length === 0) {
      return 'Potential violation detected';
    }

    return `Site offers ${parts.join(', ')}.`;
  }

  /**
   * Batch classify multiple pages
   */
  static classifyMultiple(
    crawlResults: CrawlResult[],
    contentTitle: string,
    debug = true
  ): Violation[] {
    const violations: Violation[] = [];
    const filtered: { domain: string; score: number; reason: string }[] = [];

    console.log(`\n🤖 CLASSIFICATION REPORT for "${contentTitle}":`);
    console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);

    for (const result of crawlResults) {
      const violation = this.classifyPage(result, contentTitle, debug);
      if (violation) {
        violations.push(violation);
      } else {
        // Track why it was filtered
        const domain = new URL(result.url).hostname;
        filtered.push({
          domain,
          score: this.calculateScore(result, contentTitle),
          reason: 'Score below threshold'
        });
      }
    }

    console.log(`\n📊 SUMMARY:`);
    console.log(`  ✅ Violations: ${violations.length}`);
    console.log(`  ❌ Filtered: ${filtered.length}`);

    if (filtered.length > 0) {
      console.log(`\n⚠️ FILTERED SITES (score < ${this.VIOLATION_THRESHOLD}):`);
      filtered.forEach(f => {
        console.log(`  • ${f.domain}: ${f.score} points - ${f.reason}`);
      });
    }
    console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`);

    return violations;
  }

  /**
   * Batch classify multiple pages using LLM validation (async)
   */
  static async classifyMultipleWithLLM(
    crawlResults: CrawlResult[],
    contentTitle: string,
    llmClient: OpenRouterClient,
    debug = true
  ): Promise<Violation[]> {
    const violations: Violation[] = [];
    const filtered: { domain: string; reason: string; confidence?: number }[] = [];

    console.log(`\n🤖 LLM CLASSIFICATION REPORT for "${contentTitle}":`);
    console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);

    // Build eligible set for LLM to avoid useless requests
    const unsupportedDomains = /(^|\.)((pinterest|reddit|twitter|x|facebook|instagram|tiktok)\.com)$/i;
    const eligibleIndices: number[] = [];
    const domainQuota: Record<string, number> = {};
    for (let i = 0; i < crawlResults.length; i++) {
      const r = crawlResults[i];
      const domain = (() => { try { return new URL(r.url).hostname; } catch { return ''; } })();
      const textLen = (r.text?.length || 0) + (r.html?.length || 0);
      if (textLen < 300) continue; // too short
      if (unsupportedDomains.test(domain)) continue; // skip heavy UGC we already guard
      const d = domain.toLowerCase();
      domainQuota[d] = (domainQuota[d] || 0) + 1;
      if (domainQuota[d] > 2) continue; // cap pages per domain
      eligibleIndices.push(i);
    }

    const eligiblePages = eligibleIndices.map(i => {
      const result = crawlResults[i];
      return {
        url: result.url,
        domain: new URL(result.url).hostname,
        pageTitle: result.title || '',
        pageText: result.text || result.html || '',
      };
    });

    console.log(`📡 Sending ${eligiblePages.length}/${crawlResults.length} pages to LLM for validation...`);

    const llmResults = eligiblePages.length > 0
      ? await llmClient.classifyMultiple(eligiblePages, contentTitle, { concurrency: 5 })
      : [];

    // Map results back to original indices
    const resultByIndex = new Map<number, LLMClassificationResult | null>();
    eligibleIndices.forEach((origIdx, j) => {
      resultByIndex.set(origIdx, llmResults[j] || null);
    });

    // Process results
    for (let i = 0; i < crawlResults.length; i++) {
      const result = crawlResults[i];
      const llmResult = resultByIndex.has(i) ? resultByIndex.get(i)! : null;
      const domain = new URL(result.url).hostname;

      console.log(`\n🔍 ${domain}:`);
      console.log(`  Page title: ${result.title || 'N/A'}`);

      if (!llmResult) {
        console.log(`  ⚠️ LLM validation failed, falling back to heuristics`);
        // Fall back to heuristic classification
        const violation = this.classifyPage(result, contentTitle, false);
        if (violation) {
          violation.classificationMethod = 'heuristic';
          violations.push(violation);
          console.log(`  ✅ VIOLATION (heuristics: ${violation.score}%)`);
        } else {
          filtered.push({ domain, reason: 'LLM failed, heuristics below threshold' });
          console.log(`  ❌ FILTERED OUT (heuristics below threshold)`);
        }
        continue;
      }

      console.log(`  LLM Result:`);
      console.log(`    - Is Piracy: ${llmResult.isPiracy}`);
      console.log(`    - Confidence: ${llmResult.confidence}%`);
      console.log(`    - Reasoning: ${llmResult.reasoning}`);

      // Use LLM result if confidence is high enough
      if (llmResult.isPiracy && llmResult.confidence >= this.LLM_CONFIDENCE_THRESHOLD) {
        // Create violation from LLM result
        const violation: Violation = {
          url: result.url,
          domain,
          score: llmResult.confidence,
          signals: ['llm_validation'],
          blurb: result.text?.substring(0, 300) || '',
          pageTitle: result.title,
          detectedAt: Date.now(),
          verified: true,
          verificationMethod: 'full_crawl',
          llmReasoning: llmResult.reasoning,
          classificationMethod: 'llm',
        };
        violations.push(violation);
        console.log(`  ✅ VIOLATION (LLM: ${llmResult.confidence}%)`);
      } else if (!llmResult.isPiracy && llmResult.confidence >= this.LLM_CONFIDENCE_THRESHOLD) {
        // LLM says not piracy with high confidence
        filtered.push({
          domain,
          reason: `LLM: Not piracy (${llmResult.confidence}% confidence)`,
          confidence: llmResult.confidence,
        });
        console.log(`  ❌ FILTERED OUT (LLM: Not piracy, ${llmResult.confidence}% confidence)`);
      } else {
        // LLM is uncertain, fall back to heuristics
        console.log(`  ⚠️ LLM uncertain (${llmResult.confidence}%), falling back to heuristics`);
        const violation = this.classifyPage(result, contentTitle, false);
        if (violation) {
          violation.classificationMethod = 'heuristic';
          violations.push(violation);
          console.log(`  ✅ VIOLATION (heuristics: ${violation.score}%)`);
        } else {
          filtered.push({ domain, reason: 'LLM uncertain, heuristics below threshold' });
          console.log(`  ❌ FILTERED OUT (heuristics below threshold)`);
        }
      }
    }

    console.log(`\n📊 SUMMARY:`);
    console.log(`  ✅ Violations: ${violations.length}`);
    console.log(`  ❌ Filtered: ${filtered.length}`);

    if (filtered.length > 0 && debug) {
      console.log(`\n⚠️ FILTERED SITES:`);
      filtered.forEach(f => {
        console.log(`  • ${f.domain}: ${f.reason}`);
      });
    }
    console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`);

    return violations;
  }

  /**
   * Calculate score without creating violation (for debugging)
   */
  private static calculateScore(crawlData: CrawlResult, contentTitle: string): number {
    let score = 0;

    // Video player detection
    const players = FirecrawlClient.detectVideoPlayers(crawlData.html);
    if (players.length > 0 && !(players.length === 1 && players[0] === 'YouTube')) {
      score += 40;
    } else if (players.length === 1 && players[0] === 'YouTube') {
      score += 10;
    }

    // Download links
    const downloadLinks = FirecrawlClient.extractDownloadLinks(crawlData.links, crawlData.html);
    if (downloadLinks.length > 0) {
      score += 30;
    }

    // Piracy keywords
    const text = (crawlData.text + ' ' + crawlData.html).toLowerCase();
    const titleInPage = text.includes(contentTitle.toLowerCase());
    if (titleInPage) {
      const piracyKeywords = [
        'watch free', 'free streaming', 'download full', 'free download',
        'مشاهدة مجانا', 'تحميل مجاني', 'بدون اشتراك',
      ];
      const matches = piracyKeywords.filter(kw => text.includes(kw.toLowerCase()));
      if (matches.length >= 2) {
        score += 15;
      }
    }

    // Suspicious ads
    const html = crawlData.html.toLowerCase();
    const suspiciousAds = ['popcash', 'popads', 'propellerads', 'adcash', 'clickadu'];
    if (suspiciousAds.some(ad => html.includes(ad))) {
      score += 10;
    }

    // Streaming embeds
    const embedPatterns = ['embed.php', 'player.php', 'streamtape', 'mixdrop', 'upstream'];
    if (embedPatterns.some(pattern => html.includes(pattern))) {
      score += 25;
    }

    return score;
  }

  /**
   * Classify from search result only (for unreachable sites)
   * Uses only the information from search engine (title, snippet, URL)
   * Assigns lower confidence score since we couldn't verify by crawling
   */
  static classifyFromSearchResult(
    searchResult: { url: string; title: string; snippet: string; domain: string },
    contentTitleOrAliases: string | string[],
    mode: 'visibility' | 'fallback' = 'fallback'
  ): Violation | null {
    let score = 0;
    const signals: string[] = [];
    const raw = (searchResult.title + ' ' + searchResult.snippet);
    const text = raw.toLowerCase();

    // Normalize helpers (remove punctuation/diacritics)
    const normalize = (s: string) => s
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0617-\u061A\u064B-\u0652]/g, '') // Arabic diacritics
      .replace(/["'`’”“·•.,:;!?()\[\]{}_-]/g, ' ')
      .replace(/\s+/g, ' ') // collapse spaces
      .trim();

    const tokenSet = (s: string) => new Set(normalize(s).split(' ').filter(Boolean));

    const titles = Array.isArray(contentTitleOrAliases)
      ? contentTitleOrAliases
      : [contentTitleOrAliases];

    const primaryNormalized = normalize(titles[0] || '');
    const titleTokens = primaryNormalized.split(' ').filter(Boolean);
    const stopwords = new Set(['the','a','an','of','to','in','on','at','and','or','with','for','from','part','series','season']);
    const keywordTokens = titleTokens.filter(t => !stopwords.has(t));
    const normalizedText = normalize(raw);
    if (keywordTokens.some(k => !normalizedText.includes(k))) {
      return null;
    }
    const jaccard = (a: Set<string>, b: Set<string>) => {
      const inter = new Set([...a].filter(t => b.has(t)));
      const union = new Set([...a, ...b]);
      return union.size === 0 ? 0 : inter.size / union.size;
    };

    const textTokens = tokenSet(raw);
    const bestTitleOverlap = titles
      .map(t => jaccard(tokenSet(t), textTokens))
      .reduce((m, v) => Math.max(m, v), 0);

    // Require reasonable overlap with any alias/title
    const overlapThreshold = mode === 'visibility' ? 0.4 : 0.6;
    if (bestTitleOverlap < overlapThreshold) {
      return null; // Not clearly about our content
    }

    // Piracy keywords in search result
    const piracyKeywords = [
      'watch free', 'free streaming', 'download full', 'free download',
      'watch online free', 'stream for free', 'no subscription',
      'مشاهدة مجانا', 'تحميل مجاني', 'بدون اشتراك', 'مجانا بالكامل',
    ];

    const matchedKeywords = piracyKeywords.filter(kw => text.includes(kw));
    if (matchedKeywords.length >= 1) {
      score += 20;
      signals.push('search_piracy_keywords');
    }

    // URL patterns suggesting piracy
    const urlLower = searchResult.url.toLowerCase();
    const suspiciousPatterns = [
      'free', 'watch', 'stream', 'download', 'movie', 'series',
      'film', 'online', 'hd', '1080p', 'torrent'
    ];

    const urlMatches = suspiciousPatterns.filter(pattern => urlLower.includes(pattern));
    if (urlMatches.length >= 2) {
      score += 15;
      signals.push('suspicious_url_pattern');
    }

    // Domain patterns (non-legitimate streaming)
    if (!urlLower.includes('official') && !urlLower.includes('buy') && !urlLower.includes('rent')) {
      score += 10;
      signals.push('non_commercial_domain');
    }

    // Appeared in search with piracy-related queries
    signals.push('search_result_context');
    score += 15;

    // Base score for being unreachable (might be blocking bots/scrapers)
    signals.push('unreachable_site');
    score += 10;

    // Threshold for search-only classification
    // For visibility scoring we use a balanced threshold to avoid over-classification
    // while still capturing piracy prevalence in search results
    // NOTE: We require actual piracy signals (keywords, URL patterns, etc.)
    // and do NOT classify domains as pirate just because they're non-OSN
    const threshold = mode === 'visibility' ? 45 : 50;
    if (score < threshold) {
      return null;
    }

    // Cap maximum score at 70 for unverified sites
    score = Math.min(score, 70);

    return {
      url: searchResult.url,
      domain: searchResult.domain,
      score,
      signals: [mode === 'visibility' ? 'visibility_search' : 'fallback_classification', ...signals],
      blurb: `Site found in search results but unreachable for verification. ${
        matchedKeywords.length > 0
          ? `Search snippet contains piracy indicators: "${matchedKeywords.slice(0, 2).join('", "')}".`
          : 'Domain and URL patterns suggest potential violation.'
      } Manual review recommended.`,
      pageTitle: searchResult.title,
      detectedAt: Date.now(),
      verified: false,
      verificationMethod: 'search_only',
      classificationMethod: 'search_only',
    };
  }

  /**
   * Get confidence level text from score
   */
  static getConfidenceLevel(score: number): string {
    if (score >= 85) return 'Very High';
    if (score >= 70) return 'High';
    if (score >= 60) return 'Medium';
    return 'Low';
  }

  /**
   * Get color code for confidence level
   */
  static getConfidenceColor(score: number): string {
    if (score >= 85) return '#dc2626'; // red
    if (score >= 70) return '#f97316'; // orange
    if (score >= 60) return '#facc15'; // yellow
    return '#6b7280'; // gray
  }
  /**
   * Configure runtime tunables from Settings
   */
  static setTunables(opts: {
    violationThreshold?: number;
    llmConfidenceThreshold?: number;
    ugcMinDurationMinutes?: number;
    episodeListMinCount?: number;
  }) {
    if (typeof opts.violationThreshold === 'number') {
      this.VIOLATION_THRESHOLD = Math.max(0, Math.min(100, Math.round(opts.violationThreshold)));
    }
    if (typeof opts.llmConfidenceThreshold === 'number') {
      this.LLM_CONFIDENCE_THRESHOLD = Math.max(0, Math.min(100, Math.round(opts.llmConfidenceThreshold)));
    }
    if (typeof opts.ugcMinDurationMinutes === 'number') {
      this.UGC_MIN_DURATION_MINUTES = Math.max(1, Math.round(opts.ugcMinDurationMinutes));
    }
    if (typeof opts.episodeListMinCount === 'number') {
      this.EPISODE_LIST_MIN_COUNT = Math.max(1, Math.round(opts.episodeListMinCount));
    }
  }
}
